import React, { useState, useEffect, useMemo } from "react";
import {
  Container, Row, Col, Button, Modal, Form, Alert, Badge, InputGroup,
} from "react-bootstrap";
import DynamicTable from "../../components/DynamicTable";
import { diagnostiquerSiret } from "../../utilities";
import { fetchPartners, createPartner, updatePartner, regenerateToken } from "./DataService";

const EMPTY_FORM = {
  name: "", raison_sociale: "", email: "",
  siret: "", tva_intra: "", adresse: "", code_postal: "", ville: "",
  prix_flex: "", prix_planifie: "", prix_urgent: "", webhook_url: "", is_active: true,
};

function PartnersScreen() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal créer / éditer
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newToken, setNewToken] = useState(null); // token brut affiché une seule fois

  // Modal affichage token
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenDisplay, setTokenDisplay] = useState({ name: "", token: "" });
  const [tokenCopied, setTokenCopied] = useState(false);

  const loadPartners = async () => {
    try {
      setLoading(true);
      setPartners(await fetchPartners());
    } catch {
      setError("Impossible de charger les partenaires.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPartners(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setNewToken(null);
    setShowModal(true);
  };

  const openEdit = (partner) => {
    setEditingId(partner.id);
    setForm({
      name: partner.name ?? "",
      raison_sociale: partner.raison_sociale ?? "",
      email: partner.email ?? "",
      siret: partner.siret ?? "",
      tva_intra: partner.tva_intra ?? "",
      adresse: partner.adresse ?? "",
      code_postal: partner.code_postal ?? "",
      ville: partner.ville ?? "",
      prix_flex: partner.prix_flex ?? "",
      prix_planifie: partner.prix_planifie ?? "",
      prix_urgent: partner.prix_urgent ?? "",
      webhook_url: partner.webhook_url ?? "",
      is_active: partner.is_active,
    });
    setFormError(null);
    setNewToken(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setFormError(null);
    if (!form.name.trim()) { setFormError("Le nom est obligatoire."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        prix_flex: form.prix_flex !== "" ? parseFloat(form.prix_flex) : null,
        prix_planifie: form.prix_planifie !== "" ? parseFloat(form.prix_planifie) : null,
        prix_urgent: form.prix_urgent !== "" ? parseFloat(form.prix_urgent) : null,
        webhook_url: form.webhook_url || null,
        is_active: form.is_active,
      };

      if (editingId) {
        const updated = await updatePartner(editingId, payload);
        setPartners((prev) => prev.map((p) => (p.id === editingId ? { ...p, ...updated } : p)));
        setShowModal(false);
      } else {
        const created = await createPartner(payload);
        // Afficher le token brut avant de fermer
        setNewToken(created.raw_token);
        setPartners((prev) => [...prev, {
          id: created.id, name: created.name, email: created.email,
          is_active: created.is_active, prix_flex: created.prix_flex,
          prix_planifie: created.prix_planifie, prix_urgent: created.prix_urgent,
        }]);
      }
    } catch (e) {
      const msg = e?.response?.data?.errors
        ? Object.values(e.response.data.errors).flat().join(" ")
        : "Erreur lors de l'enregistrement.";
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async (partner) => {
    if (!window.confirm(`Régénérer le token de ${partner.name} ? L'ancien token sera immédiatement invalide.`)) return;
    try {
      const { raw_token } = await regenerateToken(partner.id);
      setTokenDisplay({ name: partner.name, token: raw_token });
      setTokenCopied(false);
      setShowTokenModal(true);
    } catch {
      alert("Erreur lors de la régénération du token.");
    }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    setTokenCopied(true);
  };

  const formatPrice = (v) => (v != null ? `${parseFloat(v).toFixed(2)} €` : <span className="text-muted">—</span>);

  const columns = useMemo(() => [
    {
      Header: "Partenaire",
      accessor: "name",
      Cell: ({ row }) => (
        <div>
          <strong>{row.original.name}</strong>
          {row.original.email && <div className="text-muted small">{row.original.email}</div>}
        </div>
      ),
    },
    {
      Header: "Statut",
      accessor: "is_active",
      Cell: ({ value }) =>
        value ? <Badge bg="success">Actif</Badge> : <Badge bg="secondary">Inactif</Badge>,
    },
    {
      Header: "Flex (HT)",
      accessor: "prix_flex",
      Cell: ({ value }) => formatPrice(value),
    },
    {
      Header: "Planifiée (HT)",
      accessor: "prix_planifie",
      Cell: ({ value }) => formatPrice(value),
    },
    {
      Header: "Urgent (HT)",
      accessor: "prix_urgent",
      Cell: ({ value }) => formatPrice(value),
    },
    {
      Header: "Dernière activité",
      accessor: "last_used_at",
      Cell: ({ value }) =>
        value
          ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
          : <span className="text-muted">Jamais</span>,
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={() => openEdit(row.original)}>
            Modifier
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => handleRegenerate(row.original)}>
            Token
          </Button>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [partners]);

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Partenaires API</h4>
          <small className="text-muted">Gestion des accès et tarifs par partenaire</small>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={openCreate}>+ Nouveau partenaire</Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <p className="text-muted">Chargement…</p>}

      {!loading && (
        <DynamicTable
          columns={columns}
          data={partners}
          total={partners.length}
          last_page={1}
          nbr={1}
          setNbr={() => {}}
        />
      )}

      {/* Modal créer / éditer */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Modifier le partenaire" : "Nouveau partenaire"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}

          {newToken && (
            <Alert variant="warning">
              <strong>Token généré — à copier maintenant, il ne sera plus affiché :</strong>
              <InputGroup className="mt-2">
                <Form.Control readOnly value={newToken} style={{ fontFamily: "monospace", fontSize: 12 }} />
                <Button variant="outline-secondary" onClick={() => copyToClipboard(newToken)}>Copier</Button>
              </InputGroup>
            </Alert>
          )}

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="ex: Acme Corp"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email de facturation</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="billing@acme.com"
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />
            {/* Mentions obligatoires de la facture mensuelle du partenaire.
                Facultatives ici : une facture doit pouvoir sortir sur un
                partenaire incomplet, quitte a lui manquer une ligne. */}
            <p className="text-muted small mb-2">
              Identite legale &mdash; imprimee sur la facture mensuelle
            </p>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Raison sociale</Form.Label>
                  <Form.Control
                    value={form.raison_sociale}
                    onChange={(e) => setForm((f) => ({ ...f, raison_sociale: e.target.value }))}
                    placeholder="Denomination legale, si differente du nom d'usage"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>SIRET</Form.Label>
                  <Form.Control
                    value={form.siret}
                    onChange={(e) => setForm((f) => ({ ...f, siret: e.target.value }))}
                    placeholder="14 chiffres"
                  />
                  {/* Retour immediat : sans lui, on saisit quatorze chiffres
                      sans savoir s'ils sont bons avant le rejet du serveur. */}
                  {(() => {
                    const d = diagnostiquerSiret(form.siret);
                    if (d.etat === "absent") return null;
                    const couleur = { valide: "text-success", incomplet: "text-warning", invalide: "text-danger" }[d.etat];
                    return (
                      <Form.Text className={`${couleur} d-block mt-1`}>
                        {d.etat === "valide" ? "\u2713 " : "\u26a0 "}{d.message}
                      </Form.Text>
                    );
                  })()}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>TVA intracommunautaire</Form.Label>
                  <Form.Control
                    value={form.tva_intra}
                    onChange={(e) => setForm((f) => ({ ...f, tva_intra: e.target.value }))}
                    placeholder="FR00000000000"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Adresse de facturation</Form.Label>
                  <Form.Control
                    value={form.adresse}
                    onChange={(e) => setForm((f) => ({ ...f, adresse: e.target.value }))}
                    placeholder="12 Avenue Jean Medecin"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Code postal</Form.Label>
                  <Form.Control
                    value={form.code_postal}
                    onChange={(e) => setForm((f) => ({ ...f, code_postal: e.target.value }))}
                    placeholder="06000"
                    maxLength="5"
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Ville</Form.Label>
                  <Form.Control
                    value={form.ville}
                    onChange={(e) => setForm((f) => ({ ...f, ville: e.target.value }))}
                    placeholder="Nice"
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />
            <p className="text-muted small mb-2">Prix fixes HT par type de livraison (TVA 20% appliquée automatiquement)</p>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Flex (HT)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.prix_flex}
                      onChange={(e) => setForm((f) => ({ ...f, prix_flex: e.target.value }))}
                      placeholder="0.00"
                    />
                    <InputGroup.Text>€</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Planifiée (HT)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.prix_planifie}
                      onChange={(e) => setForm((f) => ({ ...f, prix_planifie: e.target.value }))}
                      placeholder="0.00"
                    />
                    <InputGroup.Text>€</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Urgent (HT)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.prix_urgent}
                      onChange={(e) => setForm((f) => ({ ...f, prix_urgent: e.target.value }))}
                      placeholder="0.00"
                    />
                    <InputGroup.Text>€</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>URL Webhook <span className="text-muted fw-normal">(optionnel)</span></Form.Label>
              <Form.Control
                type="url"
                value={form.webhook_url}
                onChange={(e) => setForm((f) => ({ ...f, webhook_url: e.target.value }))}
                placeholder="https://api.partenaire.com/webhooks/marion"
              />
              <Form.Text className="text-muted">
                Recevra un POST à chaque changement de statut de livraison.
              </Form.Text>
            </Form.Group>

            {editingId && (
              <Form.Check
                type="switch"
                label="Partenaire actif"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal affichage token après régénération */}
      <Modal show={showTokenModal} onHide={() => setShowTokenModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Nouveau token — {tokenDisplay.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            Ce token ne sera plus affiché après fermeture de cette fenêtre.
          </Alert>
          <InputGroup>
            <Form.Control readOnly value={tokenDisplay.token} style={{ fontFamily: "monospace", fontSize: 12 }} />
            <Button variant={tokenCopied ? "success" : "outline-secondary"} onClick={() => copyToken(tokenDisplay.token)}>
              {tokenCopied ? "Copié ✓" : "Copier"}
            </Button>
          </InputGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowTokenModal(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
}

export default PartnersScreen;
